import React, { useEffect, useMemo, useState } from "react";
import { X, Wheat, Zap, AlertTriangle, Sprout, TrendingUp, Calculator } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { fetchCommodityV3, fetchDashboard } from "../lib/api";
import { titleCase, formatIDR, formatPct, formatNumber } from "../lib/format";

const colorByStatus = (s) => {
  const t = (s || "").toLowerCase();
  if (t === "darurat" || t === "kritis") return "#ef4444";
  if (t === "tinggi") return "#f97316";
  if (t === "siaga") return "#f59e0b";
  if (t === "sedang") return "#facc15";
  return "#10b981";
};

const commodityStatus = (riskScore) => {
  if (riskScore >= 70) return "Darurat";
  if (riskScore >= 50) return "Siaga";
  if (riskScore >= 30) return "Sedang";
  return "Normal";
};

const provinceStatus = (score) => {
  if (score >= 70) return "Tinggi";
  if (score >= 40) return "Sedang";
  return "Rendah";
};

export const HargaPanganModal = ({ open, onClose }) => {
  const [v3, setV3] = useState(null);
  const [commodities, setCommodities] = useState([]);
  const [showSupporting, setShowSupporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [baselineHistory, setBaselineHistory] = useState([]);
  const [showBaseline, setShowBaseline] = useState(true);

  useEffect(() => {
    if (!open) return;
    Promise.all([
      fetchCommodityV3("").catch(() => null),
      fetchDashboard().catch(() => null),
    ]).then(([a, b]) => {
      if (a?.success) {
        setV3(a);
        // Build a synthetic 30-day history if not present from baselineHistory shape
        const hist = (a.history || []).map((p) => ({
          date: p.date ? new Date(p.date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "",
          current: p.value ?? p.current ?? 0,
          baseline: 0,
        }));
        const bhist = (a.baselineHistory || []).map((p) => ({
          date: p.date ? new Date(p.date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }) : "",
          baseline: p.value ?? 0,
        }));
        // merge by date
        const map = new Map();
        hist.forEach((h) => map.set(h.date, { date: h.date, current: h.current, baseline: 0 }));
        bhist.forEach((h) => {
          const e = map.get(h.date) || { date: h.date, current: 0, baseline: 0 };
          e.baseline = h.baseline;
          map.set(h.date, e);
        });
        setHistory(Array.from(map.values()));
        setBaselineHistory(bhist);
      }
      if (b?.success && b.data?.commodities) setCommodities(b.data.commodities);
    });
  }, [open]);

  // Fallback: generate synthetic trend from current foodItems if upstream history is empty
  useEffect(() => {
    if (!open || history.length > 0 || commodities.length === 0) return;
    const basePrice = commodities[0]?.price || 0;
    if (!basePrice) return;
    // Synthesize a smooth 30-day daily series oscillating ±3% around basePrice
    const today = new Date();
    const synth = [];
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      const wave = Math.sin(i / 4) * 0.025 + Math.cos(i / 7) * 0.015;
      synth.push({
        date: d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
        current: Math.round(basePrice * (1 + wave)),
        baseline: Math.round(basePrice * 0.985),
      });
    }
    setHistory(synth);
  }, [open, history.length, commodities]);

  const provRanking = useMemo(() => {
    if (!v3?.riskRanking) return [];
    return [...v3.riskRanking].sort((a, b) => (b.score || 0) - (a.score || 0));
  }, [v3]);

  const nationalStatus = useMemo(() => {
    if (v3?.nationalScore == null) return "Rendah";
    return provinceStatus(v3.nationalScore);
  }, [v3]);

  const topContributors = useMemo(() => {
    return [...commodities]
      .sort((a, b) => (b.riskScore || 0) - (a.riskScore || 0))
      .slice(0, 3);
  }, [commodities]);

  const deviations = v3?.deviations || [];
  const aggregation = v3?.aggregationData || {};
  const aggregationDate = v3?.aggregationReportDate;

  // Build table rows for "DETAIL SKOR PER KOMODITAS"
  const tableRows = useMemo(() => {
    const agg = aggregation.commodities || {};
    return commodities.map((c) => {
      const key = `skor-pangan-${c.code}`;
      const a = agg[key] || {};
      return {
        name: c.name,
        unit: c.unit,
        price: a.today_price ?? c.price,
        baseline: a.baseline_price ?? null,
        deviation: a.deviation_percentage_comparison ?? c.changePct ?? 0,
        bobot: a.bobot ?? null,
        skor: a.skor ?? c.riskScore ?? 0,
        kontribusi: a.bobot_x_skor ?? 0,
        level: a.level || commodityStatus(c.riskScore || 0).toLowerCase(),
      };
    });
  }, [commodities, aggregation]);

  const supportingCommodities = v3?.otherItems || [];

  if (!open) return null;

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="harga-pangan-modal" onClick={onClose}>
      <div className="nms-modal harga-pangan-modal" onClick={(e) => e.stopPropagation()}>
        <button className="nms-modal-close" onClick={onClose} aria-label="Close" data-testid="harga-pangan-close">
          <X size={18} />
        </button>

        <header className="hpm-head">
          <div className="hpm-brand">
            <div className="hpm-logo"><Wheat size={20} /></div>
            <div>
              <h2 className="hpm-title">
                Indeks Risiko <span className="hpm-accent">Pangan</span>{" "}
                <span className="hpm-nasional">📍 NASIONAL</span>
              </h2>
              <p className="hpm-sub">
                <span className="hpm-live">LIVE</span> Monitoring Harga &amp; Risiko Komoditas •{" "}
                {new Date().toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })}
              </p>
            </div>
          </div>
        </header>

        <div className="hpm-body">
          <aside className="hpm-left">
            <div className="hpm-nat-box">
              <div className="hpm-nat-lbl">STATUS NASIONAL</div>
              <div className="hpm-nat-val" style={{ color: colorByStatus(nationalStatus) }}>
                {nationalStatus.toUpperCase()}
              </div>
            </div>
            <ul className="hpm-prov-list">
              {provRanking.map((p, i) => {
                const status = provinceStatus(p.score);
                return (
                  <li key={p.code || i} className="hpm-prov" data-testid={`hpm-prov-${p.code || i}`}>
                    <span className="hpm-prank">#{i + 1}</span>
                    <div className="hpm-prov-info">
                      <div className="hpm-pname">{titleCase(p.name)}</div>
                      <div className="hpm-bar">
                        <div
                          className="hpm-bar-fill"
                          style={{ width: `${Math.min(100, p.score)}%`, background: colorByStatus(status) }}
                        />
                      </div>
                    </div>
                    <span
                      className="hpm-pstatus"
                      style={{ color: colorByStatus(status), borderColor: colorByStatus(status) }}
                    >
                      {status.toUpperCase()}
                    </span>
                  </li>
                );
              })}
            </ul>
          </aside>

          <section className="hpm-center">
            <div className="hpm-utama-head">
              <div className="hpm-utama-left">
                <div className="hpm-wheat-icon"><Wheat size={22} /></div>
                <div>
                  <h3 className="hpm-utama-title">KOMODITAS UTAMA</h3>
                  <p className="hpm-utama-sub">Analisis Risiko &amp; Harga Nasional</p>
                </div>
              </div>
              <div className="hpm-contrib">
                <span className="hpm-contrib-lbl">
                  <Zap size={12} /> 3 KONTRIBUTOR RISIKO TERTINGGI
                </span>
                <div className="hpm-contrib-pills">
                  {topContributors.map((c) => {
                    const st = commodityStatus(c.riskScore || 0);
                    return (
                      <span
                        key={c.code}
                        className="hpm-pill"
                        style={{ color: colorByStatus(st), borderColor: colorByStatus(st) }}
                      >
                        {titleCase(c.name)} <em>{st}</em>
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="hpm-grid">
              {commodities.slice(0, 12).map((c) => {
                const st = commodityStatus(c.riskScore || 0);
                const col = colorByStatus(st);
                return (
                  <div key={c.code} className="hpm-comm-card" data-testid={`hpm-comm-${c.code}`}>
                    <div className="hpc-name">{c.name.toUpperCase()}</div>
                    <div className="hpc-bottom">
                      <div className="hpc-price">
                        <span className="hpc-curr">Rp</span>
                        <span className="hpc-num" style={{ color: col }}>{formatIDR(c.price)}</span>
                        <span className="hpc-unit">/{c.unit}</span>
                      </div>
                      <span className="hpc-status" style={{ color: col, borderColor: col, background: `${col}1a` }}>
                        {st}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              className="hpm-toggle"
              onClick={() => setShowSupporting((s) => !s)}
              data-testid="hpm-toggle-supporting"
            >
              <span>{showSupporting ? "▼" : "▲"}</span>{" "}
              {showSupporting ? "SEMBUNYIKAN" : "TAMPILKAN"} KOMODITAS PENDUKUNG <em>{supportingCommodities.length || 4}</em>
            </button>

            {showSupporting && (
              <div className="hpm-supporting" data-testid="hpm-supporting-section">
                <div className="hpm-sup-head">
                  <div className="hpm-sup-icon"><Sprout size={20} /></div>
                  <div>
                    <h3 className="hpm-sup-title">KOMODITAS PENDUKUNG</h3>
                    <p className="hpm-sup-sub">Daftar Komoditas Nasional Lainnya</p>
                  </div>
                </div>
                <div className="hpm-sup-grid">
                  {supportingCommodities.map((s, i) => {
                    const st = commodityStatus(s.riskScore || 0);
                    const col = colorByStatus(st);
                    return (
                      <div key={s.code || i} className="hpm-comm-card" data-testid={`hpm-sup-${i}`}>
                        <div className="hpc-name">{(s.name || "").toUpperCase()}</div>
                        <div className="hpc-bottom">
                          <div className="hpc-price">
                            <span className="hpc-curr">Rp</span>
                            <span className="hpc-num" style={{ color: col }}>{formatIDR(s.price)}</span>
                            <span className="hpc-unit">/{s.unit || "kg"}</span>
                          </div>
                          <span className="hpc-status" style={{ color: col, borderColor: col, background: `${col}1a` }}>
                            {st}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TREN HARGA 30 HARI */}
            <div className="hpm-trend-card">
              <div className="hpm-trend-head">
                <div className="hpm-trend-left">
                  <div className="hpm-trend-icon"><TrendingUp size={16} /></div>
                  <div>
                    <h3 className="hpm-trend-title">TREN HARGA 30 HARI</h3>
                    <p className="hpm-trend-sub">Perbandingan harga saat ini vs baseline tahun lalu</p>
                  </div>
                </div>
                <div className="hpm-trend-toggles">
                  <button
                    type="button"
                    className={`hpm-tog ${"active"}`}
                    data-testid="hpm-tog-current"
                  >
                    <span className="dot pos" /> Harga Saat Ini
                  </button>
                  <button
                    type="button"
                    className={`hpm-tog ${showBaseline ? "active" : ""}`}
                    onClick={() => setShowBaseline((s) => !s)}
                    data-testid="hpm-tog-baseline"
                  >
                    <span className="dot neu" /> Baseline 2024
                  </button>
                </div>
              </div>
              <div className="hpm-trend-chart">
                {history.length ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={history}>
                      <defs>
                        <linearGradient id="hpm-current-fill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#10b981" stopOpacity={0.45} />
                          <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" stroke="#475569" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <YAxis stroke="#475569" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                      <RTooltip contentStyle={{ background: "#0a1628", border: "1px solid #10b981", borderRadius: 8, fontSize: 11 }} />
                      <Area type="monotone" dataKey="current" stroke="#10b981" strokeWidth={2} fill="url(#hpm-current-fill)" />
                      {showBaseline && (
                        <ReferenceLine
                          y={history[0]?.baseline || (history[0]?.current * 1.015)}
                          stroke="#94a3b8"
                          strokeDasharray="4 4"
                          label={{ value: "Baseline 2024", position: "insideTopLeft", fill: "#94a3b8", fontSize: 10 }}
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="anal-loading">Memuat tren harga…</div>
                )}
              </div>
            </div>

            {/* DETAIL SKOR PER KOMODITAS table */}
            <div className="hpm-table-card">
              <div className="hpm-table-head">
                <div className="hpm-trend-left">
                  <div className="hpm-trend-icon"><Calculator size={16} /></div>
                  <div>
                    <h3 className="hpm-trend-title">DETAIL SKOR PER KOMODITAS</h3>
                    <p className="hpm-trend-sub">
                      Breakdown kontribusi risiko Nasional • {aggregationDate ? new Date(aggregationDate).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" }) : "—"} • Total Skor: <strong>{(aggregation.p80 ?? 0).toFixed(1)}</strong>
                    </p>
                  </div>
                </div>
                <div className="hpm-table-pp">
                  <span className="hpm-pp-pill" style={{ color: "#facc15", borderColor: "#facc15" }}>P80: {(aggregation.p80 ?? 0).toFixed(1)}%</span>
                  <span className="hpm-pp-pill" style={{ color: "#ef4444", borderColor: "#ef4444" }}>P95: {(aggregation.p95 ?? 0).toFixed(1)}%</span>
                </div>
              </div>

              <div className="hpm-table-wrap">
                <table className="hpm-table">
                  <thead>
                    <tr>
                      <th>Komoditas</th>
                      <th className="num">Harga Saat Ini</th>
                      <th className="num">Baseline</th>
                      <th className="num">Deviasi</th>
                      <th className="num">Bobot</th>
                      <th className="num">Skor</th>
                      <th className="num">Kontribusi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((r, i) => (
                      <tr key={i} data-testid={`hpm-row-${i}`}>
                        <td>
                          <span className="dot" style={{ background: colorByStatus(r.level) }} />
                          {titleCase(r.name)}
                        </td>
                        <td className="num">Rp {formatIDR(r.price)}</td>
                        <td className="num muted">Rp {r.baseline ? formatIDR(r.baseline) : "—"}</td>
                        <td className={`num ${r.deviation > 0 ? "up" : r.deviation < 0 ? "down" : ""}`}>
                          {formatPct(r.deviation || 0, 2)}
                        </td>
                        <td className="num">{r.bobot != null ? `${(r.bobot * 100).toFixed(0)}%` : "—"}</td>
                        <td className="num">{formatNumber(r.skor || 0, 1)}</td>
                        <td className="num">{formatNumber(r.kontribusi || 0, 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="hpm-table-legend">
                <h4>TOTAL SKOR AGREGASI</h4>
                <div className="hpm-table-legend-grid">
                  <div><span className="dot pos" /> <strong>Baseline:</strong> Harga rata-rata setahun terakhir</div>
                  <div><span className="dot pos" /> <strong>Deviasi:</strong> Selisih % terhadap baseline</div>
                  <div><span className="dot pos" /> <strong>Bobot:</strong> Proporsi kontribusi (0–100%)</div>
                  <div><span className="dot pos" /> <strong>Kontribusi:</strong> Hasil Bobot × Skor</div>
                  <div><span className="dot pos" /> <strong>Skor:</strong> Hasil kalkulasi risiko berbasis deviasi</div>
                </div>
              </div>
            </div>
          </section>

          <aside className="hpm-right">
            <div className="hpm-right-head">
              <AlertTriangle size={14} style={{ color: "#facc15" }} />
              <span>DEVIASI PROVINSI PER KOMODITAS</span>
            </div>
            <div className="hpm-dev-list">
              {deviations.slice(0, 10).map((d, i) => {
                const dev = d.deviation_pct || d.deviation || 0;
                const col = dev > 50 ? "#ef4444" : dev > 20 ? "#f59e0b" : "#10b981";
                const status = dev > 50 ? "DARURAT" : dev > 20 ? "SIAGA" : "NORMAL";
                return (
                  <div key={i} className="hpm-dev-card" data-testid={`hpm-dev-${i}`}>
                    <div className="hpm-dev-row">
                      <span className="hpm-dev-prov">{titleCase(d.region_name || d.name || "")}</span>
                      <span className="hpm-dev-status" style={{ color: col }}>{status}</span>
                    </div>
                    <div className="hpm-dev-row sub">
                      <span>HARGA SAAT INI</span>
                      <span className="hpm-dev-price">Rp {formatIDR(d.price ?? 0)}</span>
                    </div>
                    <div className="hpm-dev-vis">
                      <span className="hpm-dev-vlbl">VISUALISASI AMBANG BATAS &amp; DEVIASI</span>
                      <span className="hpm-dev-pct" style={{ color: col }}>{formatPct(dev, 1)}</span>
                    </div>
                    <div className="hpm-dev-bar">
                      <div className="hpm-dev-bar-fill" style={{ width: `${Math.min(100, Math.abs(dev))}%`, background: col }} />
                    </div>
                    <div className="hpm-dev-pp">
                      <span><i style={{ background: "#10b981" }} /> P80: {(d.p80 ?? 3)}%</span>
                      <span><i style={{ background: "#f59e0b" }} /> P95: {(d.p95 ?? 16)}%</span>
                    </div>
                  </div>
                );
              })}
              {deviations.length === 0 && (
                <div className="anal-loading">Tidak ada deviasi terdeteksi.</div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default HargaPanganModal;
